import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, Select, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ExternalLink } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { StatusBadge } from '@shared/components/StatusBadge';
import type { ContentStatus } from '@shared/constants/statuses';
import { workflowQueueApi, type WorkflowQueueItem } from '@api/workflowQueue';
import { contentEditRoute } from '../contentRoute';

// BaseStatus int → 4-durum ContentStatus (rozet için).
function toBadgeStatus(n: number): ContentStatus {
  if (n === 7) return 'PUBLISHED';
  if (n === 3) return 'ARCHIVED';
  if (n === 5 || n === 6) return 'REVIEW';
  return 'DRAFT';
}
const STATUS_KEY: Record<number, string> = {
  5: 'inReview',
  6: 'approved',
  8: 'rejected',
};
const CONTENT_TYPE_KEY: Record<string, string> = {
  Article: 'article',
  Page: 'page',
  Sss: 'sss',
  Campaign: 'campaign',
};

const STATE_OPTIONS = [5, 6, 8];
const CONTENT_TYPE_OPTIONS = ['Article', 'Page', 'Sss', 'Campaign'];

export function WorkflowQueuePage() {
  const { t } = useTranslation(['workflow', 'common']);
  // Dinamik (status.<key> / contentType.<key>) anahtarlar için string-keyed t.
  const td = t as unknown as (key: string) => string;
  const navigate = useNavigate();
  const [state, setState] = useState<number | undefined>();
  const [contentType, setContentType] = useState<string | undefined>();

  const { data, isFetching } = useQuery({
    queryKey: ['workflow-queue', { state, contentType }],
    queryFn: () => workflowQueueApi.list({ state, contentType, pageSize: 100 }),
    placeholderData: (prev) => prev,
  });

  const columns: ColumnsType<WorkflowQueueItem> = [
    {
      title: t('columns.title'),
      dataIndex: 'title',
      key: 'title',
      render: (title: string, r) => {
        const route = contentEditRoute(r.contentType, r.contentId);
        return route ? (
          <Button type="link" style={{ padding: 0 }} onClick={() => void navigate(route)}>
            {title || t('columns.untitled')} <ExternalLink size={13} style={{ verticalAlign: -2 }} />
          </Button>
        ) : (
          <span>{title || t('columns.untitled')}</span>
        );
      },
    },
    {
      title: t('columns.type'),
      dataIndex: 'contentType',
      key: 'type',
      render: (ct: string) => <Tag>{td(`contentType.${CONTENT_TYPE_KEY[ct] ?? 'unknown'}`)}</Tag>,
    },
    {
      title: t('columns.status'),
      key: 'status',
      render: (_, r) => (
        <Space size={6}>
          <StatusBadge kind="content" status={toBadgeStatus(r.status)} size="sm" />
          <span style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>
            {td(`status.${STATUS_KEY[r.status] ?? 'other'}`)}
          </span>
        </Space>
      ),
    },
    {
      title: t('columns.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (v: string) => dayjs(v).format('DD.MM.YYYY HH:mm'),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        breadcrumb={[{ label: t('common:labels.home'), to: '/' }, { label: t('page.title') }]}
      />
      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <Select<number>
            allowClear
            placeholder={t('filters.state')}
            value={state}
            onChange={(v) => setState(v)}
            style={{ minWidth: 160 }}
            options={STATE_OPTIONS.map((s) => ({ value: s, label: td(`status.${STATUS_KEY[s] ?? 'other'}`) }))}
          />
          <Select<string>
            allowClear
            placeholder={t('filters.type')}
            value={contentType}
            onChange={(v) => setContentType(v)}
            style={{ minWidth: 160 }}
            options={CONTENT_TYPE_OPTIONS.map((ct) => ({ value: ct, label: td(`contentType.${CONTENT_TYPE_KEY[ct]}`) }))}
          />
        </Space>
        <Table<WorkflowQueueItem>
          rowKey={(r) => `${r.contentType}:${r.contentId}`}
          columns={columns}
          dataSource={data?.items ?? []}
          loading={isFetching}
          locale={{ emptyText: t('page.empty') }}
          pagination={false}
        />
      </Card>
    </div>
  );
}
